import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material';
import TwTooltip from '../TwTooltip/TwTooltip';

import AddIcon from '@mui/icons-material/Add';
import { ReactComponent as EditIcon } from '../../assets/edit.svg';
import { ReactComponent as TruckIcon } from '../../assets/truck.svg';
import { ReactComponent as DuplicateIcon } from '../../assets/duplicate.svg';
import { ReactComponent as EmailIcon } from '../../assets/email-icon.svg';
import { ReactComponent as SaveIcon } from '../../assets/savepdf.svg';
import { ReactComponent as ArchiveIcon } from '../../assets/archive.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete.svg';
import { ReactComponent as CreateBill } from '../../assets/billing.svg';
import { ReactComponent as SendManifest } from '../../assets/send-manifest.svg';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import MobileLoadMenu from './MobileLoadMenu/MobileLoadMenu';
import './TwLoadMenu.scss';
import Box from '@mui/material/Box';

export type TwMenuOptionLabel =
  | 'Edit'
  | 'Send to driver'
  | 'Duplicate'
  | 'Archive'
  | 'Save as PDF'
  | 'Send invoice'
  | 'Delete'
  | 'Unarchive'
  | 'Archive all'
  | 'Mark all loads paid'
  | 'View invoice'
  | 'Email load info'
  | 'Create invoice for n loads'
  | 'Export n loads to CSV'
  | 'Cancel'
  | 'Un-cancel'
  | 'Create trip'
  | 'Send manifest';

export interface TwLoadMenuOptions {
  label: TwMenuOptionLabel | string;
  action?: () => void;
  disabled?: boolean;
  tooltipText?: string;
  bulkMenuItem?: boolean;
  /**
   * Tooltip placement
   * Default: top-end
   */
  toolTipPlacement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
}

export interface TwLoadMenuProps {
  /**
   * Menu options
   */
  options: TwLoadMenuOptions[];
  paperClassName?: string;
  buttonClassName?: string;
}

const TwLoadMenu: React.FC<TwLoadMenuProps> = ({
  options,
  buttonClassName,
  paperClassName
}): JSX.Element => {
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (matchesMobileOrTablet) {
      setMobileMenuOpen(true);
    } else {
      setAnchorEl(event.currentTarget);
    }

    event.stopPropagation();
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>): void => {
    if (matchesMobileOrTablet) {
      setMobileMenuOpen(false);
    } else {
      setAnchorEl(null);
    }
    event.stopPropagation();
  };

  const menuItems = options.map((item) => {
    let labelClasses;
    if (item.disabled === true) {
      labelClasses = 'tw-load-menu__disabled';
      item.action = undefined;
    } else {
      labelClasses = item.label === 'Delete' ? 'tw-load-menu__delete' : 'tw-load-menu__label';
    }
    return (
      <MenuItem
        className="tid-load-menu__item"
        key={item.label}
        onClick={(e): void => {
          item.action && item.action();
          handleClose(e);
          e.stopPropagation();
        }}
      >
        {!item.bulkMenuItem && (
          <ListItemIcon>
            {item.label === 'Edit' && <EditIcon className="tw-load-menu__icon" />}
            {item.label === 'Create trip' && <AddIcon className="tw-load-menu__icon" />}
            {item.label === 'Send to driver' && <TruckIcon className="tw-load-menu__icon" />}
            {item.label === 'Duplicate' && <DuplicateIcon className="tw-load-menu__icon" />}
            {item.label === 'Save as PDF' && <SaveIcon className="tw-load-menu__icon" />}
            {item.label === 'Send invoice' && <CreateBill className="tw-load-menu__icon" />}
            {item.label === 'View invoice' && <CreateBill className="tw-load-menu__icon" />}
            {item.label === 'Send manifest' && <SendManifest className="tw-load-menu__icon" />}
            {item.label === 'Archive' && <ArchiveIcon className="tw-load-menu__icon" />}
            {item.label === 'Archive all' && <ArchiveIcon className="tw-load-menu__icon" />}
            {item.label === 'Unarchive' && <UnarchiveOutlinedIcon className="tw-load-menu__icon" />}
            {item.label === 'Delete' && <DeleteIcon className="tw-load-menu__icon" />}
            {item.label === 'Email load info' && <EmailIcon className="tw-load-menu__icon" />}
            {item.label === 'Cancel' && <DoDisturbIcon className="tw-load-menu__icon" />}
            {item.label === 'Un-cancel' && <DoDisturbIcon className="tw-load-menu__icon" />}
          </ListItemIcon>
        )}
        {item.disabled && item.tooltipText ? (
          <TwTooltip title={item.tooltipText} placement={item.toolTipPlacement || 'top-end'}>
            <ListItemText className={labelClasses}>{item.label}</ListItemText>
          </TwTooltip>
        ) : (
          <ListItemText className={labelClasses}>{item.label}</ListItemText>
        )}
      </MenuItem>
    );
  });

  return (
    <Paper className={`tid-load-menu ${paperClassName}`}>
      <IconButton
        disableRipple={true}
        className={`tid-load-menu__button tw-load-menu__menuButton ${buttonClassName}`}
        onClick={handleClick}
      >
        <Box
          style={{
            height: '50px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <MoreVertIcon className="tw-load-menu__menuIcon" sx={{ width: '50px' }} />
        </Box>
      </IconButton>

      {matchesMobileOrTablet ? (
        <MobileLoadMenu
          open={mobileMenuOpen}
          options={options}
          onClose={handleClose}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onOpen={(): void => {}}
        />
      ) : (
        <Menu
          open={open || false}
          anchorEl={anchorEl}
          onClose={handleClose}
          className="tw-load-menu__default"
          classes={options.length < 3 ? { list: 'tw-load-menu__list' } : {}}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
        >
          <MenuList className="tw-load-menu__list">{menuItems}</MenuList>
        </Menu>
      )}
    </Paper>
  );
};

export default TwLoadMenu;
